/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { WorkflowViewType } from './types'
import type { WorkflowsFilterValues, WorkflowsApiFilter } from './types'
import { convertDateForServer } from '../../utils/dateFormatting'

/**
 * Assembles API filter parameters from form values
 * Matches legacy DataService.assembleFiltersFromFormValues
 *
 * @param values - Filter form values
 * @param viewType - Current workflow view type (active, finished, all)
 * @returns Transformed API filter parameters
 */
export const assembleFiltersFromFormValues = (
  values: Partial<WorkflowsFilterValues>,
  viewType: WorkflowViewType
): WorkflowsApiFilter => {
  // Start with empty filter, then add properties
  const filter: WorkflowsApiFilter = {
    id: values.id && Array.isArray(values.id) ? values.id.map((i) => i.value) : []
  }

  // Copy filter values (excluding dates which need conversion)
  if (values.workflowType) filter.workflowType = values.workflowType
  if (values.workflowPhase) filter.workflowPhase = values.workflowPhase
  if (values.assignedTo) filter.assignedTo = values.assignedTo
  if (values.initiatorId) filter.initiatorId = values.initiatorId

  // Convert dates to server format
  if (values.startFrom) {
    filter.startFrom = convertDateForServer(values.startFrom)
  }

  if (values.startTo) {
    filter.startTo = convertDateForServer(values.startTo, true)
  }

  // Map site/study to folder IDs
  if (values.site) {
    filter.siteFolderId = values.site
  }

  if (values.study) {
    filter.studyFolderId = values.study
  }

  // Handle ETMF product hierarchy — mirrors legacy DataService.assembleFiltersFromFormValues
  if (values?.data?.product) {
    // Build fsiIdArray by extracting fsiId from each cascade level in order:
    // [product.fsiId, study_fsiId, environment.fsiId, country.fsiId, site.fsiId]
    // study is stored as an array of primitive fsiId values (numbers), others are SelectOption objects
    const product = values.data.product as Record<string, unknown>
    const environment = values.data.environment as Record<string, unknown> | null
    const country = values.data.country as Record<string, unknown> | null
    const site = values.data.site as Record<string, unknown> | null
    const study = values.data.study as unknown as number[] | null

    const fsiIdArray: (number | undefined)[] = [
      product.fsiId as number | undefined,
      undefined, // placeholder for study — filled below when length === 1
      environment?.fsiId as number | undefined,
      country?.fsiId as number | undefined,
      site?.fsiId as number | undefined
    ]

    if (!study?.length) {
      // No study selected — use product fsiId only
      filter.fsiIds = [fsiIdArray[0] as number]
    } else {
      if (study.length === 1) {
        fsiIdArray[1] = study[0] // fill study slot with its single fsiId
      }

      // Walk forward to find the deepest non-null/undefined fsiId
      let lastFsiId: number[] = []
      for (let i = 0; i < fsiIdArray.length; i++) {
        if (fsiIdArray[i] == null) {
          lastFsiId = i > 0 ? [fsiIdArray[i - 1] as number] : []
          break
        }
        lastFsiId = [fsiIdArray[fsiIdArray.length - 1] as number]
      }

      // Multiple studies → send all study fsiIds; single → send deepest cascade fsiId
      filter.fsiIds = study.length > 1 ? [...study] : lastFsiId
    }
  }

  // folderId from folder picker: augments product cascade fsiIds, or standalone
  if (values?.folderId) {
    const fid = (values.folderId as { id: number }).id
    if (fid) {
      if (filter.fsiIds?.length) {
        filter.fsiIds.push(fid) // folder + product cascade → send both
      } else {
        filter.fsiIds = [fid] // folder only
      }
    }
  }

  if (values?.documentTypeTemplateId) {
    const dtId = extractDocumentTypeTemplateId(
      values.documentTypeTemplateId as Parameters<typeof extractDocumentTypeTemplateId>[0]
    )
    if (dtId !== null) {
      filter.documentTypeTemplateId = Number(dtId)
    }
  }

  // Apply view-specific filters
  switch (viewType) {
    case WorkflowViewType.ACTIVE:
      filter.status = ['ACTIVE', 'SUSPENDED']
      break
    case WorkflowViewType.FINISHED:
      filter.status = ['FINISHED', 'CANCELLED']
      break
    case WorkflowViewType.ALL:
      // No status filter for all view
      break
  }

  return filter
}

export interface Selection {
  id?: string | number
  value?: string | number
}

/**
 * Extracts id from documentTypeTemplateId option for filter payload (single-select only)
 * @param value - The value from form (Selection object or primitive)
 * @returns id or value, or null
 */
export function extractDocumentTypeTemplateId(value: Selection | string | number | null): string | number | null {
  if (value && typeof value === 'object') {
    return value.id ?? value.value ?? null
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return value
  }
  return null
}

/**
 * Get default filter values
 *
 * @param isEosEnabled - Whether EOS feature is enabled
 * @param isEtmfEnabled - Whether ETMF feature is enabled
 * @returns Default filter values
 */
export const getDefaultFilter = (isEosEnabled: boolean, isEtmfEnabled: boolean): WorkflowsFilterValues => {
  return {
    workflowType: [],
    workflowPhase: [],
    assignedTo: [],
    initiatorId: [],
    id: [],
    startFrom: null,
    startTo: null,
    study: null,
    site: null,
    isEosSelected: false,
    isEosEnabled,
    isEtmfEnabled,
    data: {
      product: null,
      study: null,
      environment: null,
      country: null,
      site: null
    },
    folderId: null,
    documentTypeTemplateId: null
  }
}
